import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Container, CssBaseline, Grid, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { To, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import AuthContext from '../../Context/AuthContext';
import SnackbarContext from '../../Context/SnackbarContext';
import FormInput from '../../components/Form/FormInput';
import FormSelect from '../../components/Form/FormSelect';
import SharedButton from '../../components/Shared/SharedButton/SharedButton';
import { Agents_LoginRequest, Client, CustomerDependantEntity_DependantType, DependantCreate_Request, DependantCreate_Request_ForeignNationalDto, Gender, IAgents_LoginRequest, IDependantCreate_Request, IDependantCreate_Request_ForeignNationalDto } from '../../api/insurance-api.ts';
import { baseDependentSchema, getDynamicDependentSchema, getDynamicRegisterSchema } from '../../validation/schema';
import SharedSwitch from '../../components/Shared/SharedSwitch/SharedSwitch';
import { useLoader } from '../../Context/LoadingContext.jsx';
import { getDynamicApiUrl } from '../../utils/themeLoader.js';

interface DependentProps {
  setCompletedForms: (value: string) => void;
  type: CustomerDependantEntity_DependantType;
  title: string;
  form: string;
}

const Dependent: React.FC<DependentProps> = ({ form, setCompletedForms, type, title }) => {

  const { customerId } = useContext(AuthContext);
  const { showLoader, hideLoader } = useLoader();
  const { openSnackbar } = useContext(SnackbarContext);
  const [showPassportNumber, setShowPassportNumber] = useState(false);
  const apiclient = new Client(getDynamicApiUrl());

  let navigate = useNavigate();

  const routeChange = (path: To) => {
    navigate(path);
  };

  let currentSchema = getDynamicDependentSchema(showPassportNumber, type);

  type DependentsInputs = z.infer<typeof currentSchema>;

  const { control, watch, reset, formState: { errors }, register, handleSubmit, setValue, setError, clearErrors, getValues } = useForm({
    mode: 'onChange',
    resolver: zodResolver(currentSchema)
  });

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    const strippedInput = value.replace(/[^0-9]/g, "");

    let formattedDate = strippedInput;

    if (strippedInput.length > 4) {
      formattedDate = `${strippedInput.slice(0, 4)}-${strippedInput.slice(4)}`;
    }
    if (strippedInput.length > 6) {
      formattedDate = `${formattedDate.slice(0, 7)}-${formattedDate.slice(7)}`;
    }

    formattedDate = formattedDate.slice(0, 10);
    setValue(name, formattedDate);
  };


  const onSubmit: SubmitHandler<IDependantCreate_Request> = async (data: DependentsInputs) => {

    let dependentRequest: IDependantCreate_Request = {
      firstName: data.firstName,
      middleName: data.middleName && data.middleName.trim() === '' ? undefined : data.middleName && data.middleName.trim(),
      lastName: data.lastName,
      email: 'null@remove.com', // remove
      idNumber: showPassportNumber ? undefined : data.idNumber,
      type: type
    };

    if (showPassportNumber) {
      let foreignNationalDto: IDependantCreate_Request_ForeignNationalDto = {
        passportNumber: data.passportNumber,
        nationality: data.nationality,
        permitType: data.permitType,
        birthDate: new Date(data.birthDate),
        gender: data.gender
      }
      dependentRequest.foreignNational = new DependantCreate_Request_ForeignNationalDto(foreignNationalDto);
    }
    else {
      dependentRequest.foreignNational = undefined;
      dependentRequest.idNumber = data.idNumber
    }

    try {
      showLoader();
      const response = await apiclient.customerDependantCreate(customerId, new DependantCreate_Request(dependentRequest));
      setCompletedForms(form);
      openSnackbar("Dependent saved", "success");
    } catch (error) {
      console.log("error : ", error);
      openSnackbar("Failed - please try again", "error");
    }
    finally {
      hideLoader();
    }
  };

  return (
    <>
      <Container component="main" maxWidth="sm">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant='h5'>Dependent Details</Typography>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 3 }}>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormInput
                  name="firstName"
                  control={control}
                  label="Full Name"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message?.toString()}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  name="middleName"
                  control={control}
                  label="Middle Name"
                  error={!!errors.middleName}
                  helperText={errors.middleName?.message?.toString()}
                  fullWidth

                />
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  name="lastName"
                  control={control}
                  label="Last Name"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message?.toString()}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} container alignItems="center">
                <Grid xs={2}>
                  <SharedSwitch toggle={() => { setShowPassportNumber(!showPassportNumber); }} checked={showPassportNumber}></SharedSwitch>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant="body1">I want to use my passport number instead</Typography>
                </Grid>
              </Grid>

              {
                !showPassportNumber &&

                <Grid item xs={12}>
                  <FormInput
                    name="idNumber"
                    control={control}
                    label="ID Number"
                    error={!!errors.idNumber}
                    helperText={errors.idNumber?.message?.toString() || " "}
                    fullWidth
                    required
                    autoComplete="off"
                  />
                </Grid>
              }
              {
                showPassportNumber &&


                <>
                  <Grid item xs={12}>
                    <FormInput
                      name="passportNumber"
                      control={control}
                      label="passportNumber"
                      error={!!errors.passportNumber}
                      helperText={errors.passportNumber?.message?.toString()}
                      fullWidth
                      required
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInput
                      name="nationality"
                      control={control}
                      label="Nationality"
                      error={!!errors.nationality}
                      helperText={errors.nationality?.message?.toString()}
                      fullWidth
                      required
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSelect
                      name="permitType"
                      control={control}
                      label="Permit Type"
                      options={[
                        { label: "Work Visa", value: "Work Visa" },
                        { label: "Study Visa", value: "Study Visa" },
                      ]}
                      error={Boolean(errors.title)}
                      helperText={errors.title?.message?.toString() || "Title"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInput
                      name="birthDate"
                      control={control}
                      label="Birth Date"
                      error={!!errors.birthDate}
                      helperText={errors.birthDate?.message?.toString()}
                      fullWidth
                      required
                      autoComplete="off"
                      onChange={(e) => {
                        handleDateChange(e);
                        // field.onChange(e); // Ensure React Hook Form registers the change
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSelect
                      name="gender"
                      control={control}
                      label="Gender"
                      options={[
                        { label: "Male", value: Gender.Male },
                        { label: "Female", value: Gender.Female },
                      ]}
                      error={Boolean(errors.gender)}
                      helperText={errors.gender?.message?.toString() || "Title"}
                    />
                  </Grid>
                </>
              }
              {
                type == CustomerDependantEntity_DependantType.ExtendedFamily &&
                <Grid item xs={12}>
                  <FormInput
                    name="relationship"
                    control={control}
                    label="relationship"
                    error={!!errors.relationship}
                    helperText={errors.relationship?.message?.toString()}
                    fullWidth
                    required
                    autoComplete="off"
                  />
                </Grid>
              }
            </Grid>
            <Box display="flex" justifyContent="center" alignItems="center" marginTop={'20px'} marginBottom={"40px"}>
              <SharedButton type={'submit'} text={`Save`}></SharedButton>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Dependent;
